import React, {useEffect, useRef, useState} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import './index.scss';
import Input from '../../components/Input';
import Select from 'react-select';
import {
  useLazyGetFacilityQuery,
  useLazyGetCorrectiveActionsQuery,
  useLazyGetMissedInspectionsQuery,
  useUpdateMissedInspectionMutation,
  useLazyGetOutOfServiceQuery,
  useLazyGetEquipmentAllQuery,
  useLazyGetReturnToServiceQuery,
  useLazyGetEquipmentTypeQuery,
} from '../../store/builder/inspections.api';
import {useDispatch} from 'react-redux';
import {clearFields, setLoader} from '../../store/builder/reducer';
import CorrectiveActionRow from '../../components/CorrectiveActionRow';
import moment from 'moment';
import Datepicker from '../../components/Datepicker';
import DatePicker from 'react-datepicker';
import {toast} from 'react-toastify';
import {useErrorHandler} from '../../global/ErrorsHandler';
import {Helmet} from 'react-helmet';
import CorrectiveActionModal from '../../components/CorrectiveActionModal';
import {useNavigate} from 'react-router-dom';
import Table from '../../components/Table/Table';
import useLoader from '../../global/UseLoadingHandler';
import {useTranslation} from '../../hooks/useTranslation';

const getHeaders = t => [
  // Open items
  [
    t('correctiveActions.tableHeaders.createdDate'),
    t('correctiveActions.tableHeaders.facility'),
    t('correctiveActions.tableHeaders.equipmentType'),
    t('correctiveActions.tableHeaders.equipmentId'),
    t('correctiveActions.tableHeaders.inspectionCategory'),
    t('correctiveActions.tableHeaders.discrepancy'),
    t('correctiveActions.tableHeaders.inspectionComment'),
    t('correctiveActions.tableHeaders.createdBy'),
  ],
  // Closed items
  [
    t('correctiveActions.tableHeaders.createdDate'),
    t('correctiveActions.tableHeaders.closedDate'),
    t('correctiveActions.tableHeaders.facility'),
    t('correctiveActions.tableHeaders.equipmentType'),
    t('correctiveActions.tableHeaders.equipmentId'),
    t('correctiveActions.tableHeaders.inspectionCategory'),
    t('correctiveActions.tableHeaders.discrepancy'),
    t('correctiveActions.tableHeaders.inspectionComment'),
    t('correctiveActions.tableHeaders.correctiveAction'),
    t('correctiveActions.tableHeaders.closedBy'),
  ],
  [],
  // Missed insepctions
  [
    t('correctiveActions.tableHeaders.inspection'),
    t('correctiveActions.tableHeaders.equipmentId'),
    t('correctiveActions.status'),
    t('correctiveActions.tableHeaders.facility'),
    t('correctiveActions.tableHeaders.inspectionDate'),
    t('correctiveActions.tableHeaders.dueDate'),
    t('correctiveActions.tableHeaders.period'),
  ],
  // Out of Service
  [
    t('correctiveActions.tableHeaders.equipmentSerial'),
    t('correctiveActions.tableHeaders.type'),
    t('correctiveActions.tableHeaders.facility'),
    t('correctiveActions.tableHeaders.reason'),
  ],
  // Return to Service inspections
  [
    t('correctiveActions.tableHeaders.inspection'),
    t('correctiveActions.tableHeaders.equipmentId'),
    t('correctiveActions.tableHeaders.facility'),
    t('correctiveActions.tableHeaders.inspectionDate'),
    t('correctiveActions.tableHeaders.dueDate'),
    t('correctiveActions.tableHeaders.period'),
  ],
  // Grace Perios
  [
    t('correctiveActions.tableHeaders.inspection'),
    t('correctiveActions.tableHeaders.equipmentId'),
    t('correctiveActions.tableHeaders.facility'),
    t('correctiveActions.tableHeaders.inspectionDate'),
    t('correctiveActions.tableHeaders.dueDate'),
    t('correctiveActions.tableHeaders.period'),
  ],
];

const cellKeys = [
  // Open items
  [
    'created_at',
    'facility_title',
    'equipment_type_title',
    'equipment_title',
    'inspection_title',
    'discrepancy',
    'comment',
    'created_by',
  ],
  // Closed items
  [
    'created_at',
    'closed_time',
    'facility_title',
    'equipment_type_title',
    'equipment_title',
    'inspection_title',
    'discrepancy',
    'comment',
    'action',
    'closed_by_name',
  ],
  [],
  // Missed insepctions
  [
    'name',
    'equipment',
    'status',
    'facility',
    'inspection_date',
    'due_date_fbo_timezone',
    'period',
  ],
  // Out of Service
  ['title', 'type', 'facility', 'reason'],
  // Return to Service inspections
  [
    'name',
    'equipment',
    'facility',
    'inspection_date',
    'due_date_fbo_timezone',
    'period',
  ],
  // Grace Perios
  [
    'name',
    'equipment',
    'facility',
    'inspection_date',
    'due_date_fbo_timezone',
    'period',
  ],
];

const customRows = [
  'open_items',
  'closed_items',
  '',
  'missed_inspections',
  'out_of_service',
  'service_inspections',
  'grace_period',
];

export default function CorrectiveActions() {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ref = useRef(null);
  const missedInspectionsSearchTimeoutRef = useRef(null);
  const equipmentTypeSearchTimeoutRef = useRef(null);
  const equipmentSearchTimeoutRef = useRef(null);
  const urlParams = new URLSearchParams(window.location.search);
  const [currentTab, setCurrentTab] = useState(
    urlParams.get('tab_id') ? Number(urlParams.get('tab_id')) : 0,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [currentMissedIns, setCurrentMissedIns] = useState(null);
  const [updateModal, setUpdateModal] = useState(false);
  const [modalStatus, setModalStatus] = useState(false);
  const [serviceModalStatus, setServiceModalStatus] = useState(false);
  const [actionsFilters, setActionsFilters] = useState({
    date_from: null,
    date_to: null,
    facility_id: null,
    equipment_id: null,
    search: '',
  });

  const [search, setSearch] = useState('');
  const [updateInspectionData, setUpdateInspectionData] = useState({
    explanation: null,
    completed_date: '',
  });
  const [chosenCorrectiveAction, setChosenCorrectiveAction] = useState({});
  const [completedOptions, setCompletedOptions] = useState([
    {
      value: 1,
      label: t('correctiveActions.completedOptions.yes'),
    },
    {
      value: 0,
      label: t('correctiveActions.completedOptions.no'),
    },
  ]);
  const [missedInspectionsFilters, setMissedInspectionsFilters] = useState({
    date_from: null,
    date_to: null,
    completed: null,
    facility_id: null,
    equipment_type_id: null,
    period: null,
    search: '',
  });
  const [gracePeriodFilters, setGracePeriodFilters] = useState({
    date_from: null,
    date_to: null,
    completed: null,
    facility_id: null,
    equipment_type_id: null,
    period: null,
  });
  const [currentService, setCurrentService] = useState(null);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [equipmentTypeOptions, setEquipmentTypeOptions] = useState([]);
  const [equipmentTypeSearch, setEquipmentTypeSearch] = useState('');
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [periodOptions, setPeriodOptions] = useState([
    {
      label: t('correctiveActions.periodOptions.daily'),
      value: 'daily',
    },
    {
      label: t('correctiveActions.periodOptions.weekly'),
      value: 'weekly',
    },
  ]);
  const [returnToServicePeriod, setReturnToServicePeriod] = useState([
    // {value: 'daily', label: "Daily"},
    // {value: "weekly", label: "Weekly"},
    {value: 'monthly', label: t('correctiveActions.periodOptions.monthly')},
    {value: 'quarterly', label: t('correctiveActions.periodOptions.quarterly')},
    // {value: "semiannual", label: "Semiannual"},
    {value: 'annual', label: t('correctiveActions.periodOptions.annual')},
  ]);
  const [
    getActions,
    {
      data: actionsData,
      isError: actionsIsError,
      isSuccess: actionsIsSuccess,
      isFetching: actionsIsLoading,
      error: actionsError,
    },
  ] = useLazyGetCorrectiveActionsQuery();
  const [
    getOutOfService,
    {
      data: outOfServiceData,
      error: outOfServiceError,
      isLoading: outOfServiceIsLoading,
      isSuccess: outOfServiceIsSuccess,
      isError: outOfServiceISError,
    },
  ] = useLazyGetOutOfServiceQuery();
  const [
    getMissedInspections,
    {
      data: getMissedInspectionsData,
      isError: getMissedInspectionsIsError,
      isSuccess: getMissedInspectionsIsSuccess,
      isLoading: getMissedInspectionsIsLoading,
      error: getMissedInspectionsError,
    },
  ] = useLazyGetMissedInspectionsQuery();
  const [
    getReturnService,
    {
      data: getReturnServiceData,
      isError: getReturnServiceIsError,
      isSuccess: getReturnServiceIsSuccess,
      isLoading: getReturnServiceIsLoading,
      error: getReturnServiceError,
    },
  ] = useLazyGetReturnToServiceQuery();

  const [
    getFacility,
    {
      data: facilityData,
      isError: facilityIsError,
      isSuccess: facilityIsSuccess,
      isLoading: facilityIsLoading,
      error: facilityError,
    },
  ] = useLazyGetFacilityQuery();
  const [
    updateInspection,
    {
      data: inspectionData,
      isError: inspectionIsError,
      isSuccess: inspectionIsSuccess,
      isFetching: inspectionIsLoading,
      error: inspectionError,
    },
  ] = useUpdateMissedInspectionMutation();

  const [
    getEquipment,
    {
      data: equipmentData,
      isError: equipmentIsError,
      isSuccess: equipmentIsSuccess,
      isFetching: equipmentIsLoading,
      error: equipmentError,
    },
  ] = useLazyGetEquipmentAllQuery();
  const [
    getEquipmentType,
    {
      data: equipmentTypeData,
      isError: equipmentTypeIsError,
      isSuccess: equipmentTypeIsSuccess,
      isFetching: equipmentTypeIsLoading,
      error: equipmentTypeError,
    },
  ] = useLazyGetEquipmentTypeQuery();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    inspectionError,
    getMissedInspectionsError,
    actionsError,
    equipmentTypeError,
  ]);
  useEffect(() => {
    getFacility();
    getOutOfService();
  }, []);

  useEffect(() => {
    if (equipmentSearchTimeoutRef.current) {
      clearTimeout(equipmentSearchTimeoutRef.current);
    }
    const params = {...(equipmentSearch && {search: equipmentSearch})};
    equipmentSearchTimeoutRef.current = setTimeout(
      () => getEquipment(params),
      equipmentSearch ? 300 : 0,
    );
    return () => {
      if (equipmentSearchTimeoutRef.current) {
        clearTimeout(equipmentSearchTimeoutRef.current);
      }
    };
  }, [equipmentSearch]);

  useEffect(() => {
    if (equipmentTypeSearchTimeoutRef.current) {
      clearTimeout(equipmentTypeSearchTimeoutRef.current);
    }
    const params = equipmentTypeSearch ? {search: equipmentTypeSearch} : {};
    equipmentTypeSearchTimeoutRef.current = setTimeout(
      () => {
        getEquipmentType(params);
      },
      equipmentTypeSearch ? 300 : 0,
    );
    return () => {
      if (equipmentTypeSearchTimeoutRef.current) {
        clearTimeout(equipmentTypeSearchTimeoutRef.current);
      }
    };
  }, [equipmentTypeSearch]);
  useEffect(() => {
    if (facilityData) {
      setFacilityOptions(
        facilityData?.map(item => ({value: item.id, label: item.name})),
      );
    }
    if (equipmentData) {
      setEquipmentOptions(
        equipmentData?.data?.map(item => ({
          value: item.id,
          label: item.title,
        })),
      );
    }
    if (equipmentTypeData) {
      setEquipmentTypeOptions(
        equipmentTypeData?.map(item => ({
          value: item.id,
          label: item.title,
        })),
      );
    }
  }, [facilityData, equipmentData, equipmentTypeData]);
  useEffect(() => {
    setActionsFilters(old => ({
      ...old,
      facility_id: urlParams.get('facility_id'),
      equipment_id: urlParams.get('equipment_id'),
      date_from: urlParams.get('date_from'),
      date_to: urlParams.get('date_to'),
      search: urlParams.get('search') || '',
    }));
    if (urlParams.get('search')) {
      setSearch(urlParams.get('search'));
    }

    setMissedInspectionsFilters(old => ({
      ...old,
      date_from: urlParams.get('date_missed_from'),
      date_to: urlParams.get('date_missed_to'),
      facility_id: urlParams.get('facility_id'),
      equipment_type_id: urlParams.get('equipment_id'),
      period: urlParams.get('period'),
      search: urlParams.get('search_missed') || '',
    }));
    if (urlParams.get('search_missed')) {
      setSearch(urlParams.get('search_missed'));
    }
    setGracePeriodFilters(old => ({
      ...old,
      date_from: urlParams.get('date_grace_from'),
      date_to: urlParams.get('date_grace_to'),
      facility_id: urlParams.get('facility_id'),
      equipment_type_id: urlParams.get('equipment_id'),
      period: urlParams.get('period'),
    }));
  }, []);
  useEffect(() => {
    if (!updateModal) {
      setUpdateInspectionData({
        explanation: null,
        completed_date: '',
      });
    } else {
      if (currentMissedIns?.explanation) {
        setUpdateInspectionData(old => ({
          ...old,
          completed_date: currentMissedIns?.completed_date,
        }));
      }
    }
  }, [updateModal, currentMissedIns]);

  useLoader([
    actionsIsLoading,
    getMissedInspectionsIsLoading,
    getReturnServiceIsLoading,
    outOfServiceIsLoading,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (missedInspectionsSearchTimeoutRef.current) {
        clearTimeout(missedInspectionsSearchTimeoutRef.current);
      }
      if (equipmentTypeSearchTimeoutRef.current) {
        clearTimeout(equipmentTypeSearchTimeoutRef.current);
      }
      if (equipmentSearchTimeoutRef.current) {
        clearTimeout(equipmentSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (inspectionIsLoading) {
      dispatch(setLoader(true));
    }
    if (inspectionIsSuccess) {
      dispatch(setLoader(false));
      toast.success(inspectionData.message);
      setUpdateModal(false);
    }
  }, [inspectionIsSuccess, inspectionIsLoading]);

  useEffect(() => {
    dispatch(clearFields([]));
    if (currentTab === 0) {
      getActions({
        closed: false,
        page: 1,
        date_to: actionsFilters?.date_to,
        date_from: actionsFilters?.date_from,
        search: actionsFilters?.search || '',
        facility_id: actionsFilters?.facility_id,
        equipment_id: actionsFilters?.equipment_id,
      });
    }
    if (currentTab === 1) {
      getActions({
        closed: true,
        page: 1,
        date_to: actionsFilters?.date_to,
        date_from: actionsFilters?.date_from,
        search: actionsFilters?.search || '',
        facility_id: actionsFilters?.facility_id,
        equipment_id: actionsFilters?.equipment_id,
      });
    }
    if (currentTab === 6) {
      getMissedInspections({page: 1, tab_name: 'grace'});
    }
    if (currentTab === 5) {
      getReturnService({page: 1});
    }

    setActionsFilters({
      date_from: null,
      date_to: null,
      facility_id: null,
      equipment_id: null,
      search: '',
    });
    setSearch('');
    setMissedInspectionsFilters({
      date_from: null,
      date_to: null,
      completed: null,
      facility_id: null,
      equipment_type_id: null,
      period: null,
      search: '',
    });
    setGracePeriodFilters({
      date_from: null,
      date_to: null,
      completed: null,
      facility_id: null,
      equipment_type_id: null,
      period: null,
    });
    navigate(`/actions?tab_id=${currentTab}`);
  }, [currentTab]);

  useEffect(() => {
    if (updateModal || serviceModalStatus || modalStatus) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [updateModal, serviceModalStatus, modalStatus]);

  const handleFilterDateFrom = date => {
    const dateFrom = date ? moment(date).format('YYYY-MM-DD') : null;
    setActionsFilters(old => ({
      ...old,
      date_from: dateFrom,
    }));
    setCurrentPage(1);
    navigate(
      `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${dateFrom ? `&date_from=${dateFrom}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
    );
  };
  const handleFilterDateTo = date => {
    const dateTo = date ? moment(date).format('YYYY-MM-DD') : null;
    setCurrentPage(1);
    setActionsFilters(old => ({
      ...old,
      date_to: dateTo,
    }));
    navigate(
      `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${dateTo ? `&date_to=${dateTo}` : ''}`,
    );
  };
  const handleSearch = e => {
    const searchValue = e.target.value;
    setSearch(searchValue);
    setCurrentPage(1);
    // Update actionsFilters with search value after debounce
    setTimeout(() => {
      setActionsFilters(old => ({
        ...old,
        search: searchValue,
      }));
    }, 600);
  };

  const handleSearchMissedInspections = e => {
    const searchValue = e.target.value;
    setSearch(searchValue);
    setCurrentPage(1);
    if (missedInspectionsSearchTimeoutRef.current) {
      clearTimeout(missedInspectionsSearchTimeoutRef.current);
    }

    missedInspectionsSearchTimeoutRef.current = setTimeout(() => {
      setMissedInspectionsFilters(old => ({
        ...old,
        search: searchValue,
      }));
    }, 600);
  };

  const handleEquipmentTypeInputChange = (newValue, actionMeta) => {
    if (actionMeta?.action !== 'input-change') return;
    if (equipmentTypeSearchTimeoutRef.current) {
      clearTimeout(equipmentTypeSearchTimeoutRef.current);
    }
    equipmentTypeSearchTimeoutRef.current = setTimeout(() => {
      setEquipmentTypeSearch(newValue || '');
    }, 300);
  };

  const handleEquipmentInputChange = (newValue, actionMeta) => {
    if (actionMeta?.action !== 'input-change') return;
    if (equipmentSearchTimeoutRef.current) {
      clearTimeout(equipmentSearchTimeoutRef.current);
    }
    equipmentSearchTimeoutRef.current = setTimeout(() => {
      setEquipmentSearch(newValue || '');
    }, 300);
  };

  const changeTab = tabId => {
    setCurrentTab(tabId);
  };
  const renderCorrectiveActions = () => {
    if (currentTab === 0) {
      if (actionsData?.data?.length > 0) {
        return actionsData?.data?.map(action => (
          <CorrectiveActionRow
            key={action.id}
            action={action}
            setModalStatus={setModalStatus}
            currentTab={currentTab}
            setChosenCorrectiveAction={setChosenCorrectiveAction}
            chosenCorrectiveAction={chosenCorrectiveAction}
          />
        ));
      } else {
        return (
          <div className='facility__table-empty'>
            <img src='/assets/icons/facility-empty.svg' alt='empty' />
            <p>{t('correctiveActions.messages.noData')}</p>
          </div>
        );
      }
    }
    if (currentTab === 1) {
      if (actionsData?.data?.length > 0) {
        return actionsData?.data?.map(action => {
          if (action.closed_by) {
            return (
              <CorrectiveActionRow
                key={action.id}
                action={action}
                currentTab={currentTab}
                setModalStatus={setModalStatus}
                setChosenCorrectiveAction={setChosenCorrectiveAction}
                chosenCorrectiveAction={chosenCorrectiveAction}
              />
            );
          }
        });
      } else {
        return (
          <div className='facility__table-empty'>
            <img src='/assets/icons/facility-empty.svg' alt='empty' />
            <p>{t('correctiveActions.messages.noData')}</p>
          </div>
        );
      }
    }
  };

  const handleUpdateIns = e => {
    if (e.target) {
      setUpdateInspectionData(old => ({
        ...old,
        [e.target.name]: e.target.value,
      }));
    } else {
      setUpdateInspectionData(old => ({
        ...old,
        completed_date: moment(e).format('YYYY-MM-DD HH:mm:ss'),
      }));
    }
  };

  const updateMissedInspection = e => {
    e.preventDefault();
    if (
      updateInspectionData.explanation &&
      updateInspectionData.completed_date
    ) {
      updateInspection({
        id: currentMissedIns.id,
        data: updateInspectionData,
      }).then(() => {
        setTimeout(() => {
          navigate('/inspections/' + currentMissedIns?.inspection?.id);
        }, 1500);
      });
    } else {
      toast.error('Fill all the fields!');
    }
  };

  const handlePageClick = page => {
    setCurrentPage(page.selected + 1);
    if (currentTab === 3) {
      getMissedInspections({
        page: page.selected + 1,
        tab_name: 'missed',
        completed: missedInspectionsFilters.completed,
        date_from: missedInspectionsFilters.date_from,
        date_to: missedInspectionsFilters.date_to,
        facility_id: missedInspectionsFilters.facility_id,
        equipment_type_id: missedInspectionsFilters.equipment_type_id,
        period: missedInspectionsFilters.period,
        search: missedInspectionsFilters.search || '',
      });
    } else if (currentTab === 6) {
      getMissedInspections({
        page: page.selected + 1,
        tab_name: 'grace',
        completed: gracePeriodFilters.completed,
        date_from: gracePeriodFilters.date_from,
        date_to: gracePeriodFilters.date_to,
        facility_id: gracePeriodFilters.facility_id,
        equipment_type_id: gracePeriodFilters.equipment_type_id,
        period: gracePeriodFilters.period,
      });
    } else if (currentTab === 5) {
      getReturnService({
        page: page.selected + 1,
        completed: missedInspectionsFilters.completed,
        date_from: missedInspectionsFilters.date_from,
        date_to: missedInspectionsFilters.date_to,
        facility_id: missedInspectionsFilters.facility_id,
        equipment_type_id: missedInspectionsFilters.equipment_type_id,
        period: missedInspectionsFilters.period,
      });
    } else {
      getActions({
        closed: currentTab !== 0,
        page: page.selected + 1,
        date_to: actionsFilters?.date_to,
        date_from: actionsFilters?.date_from,
        search: actionsFilters?.search || '',
        facility_id: actionsFilters?.facility_id,
        equipment_id: actionsFilters?.equipment_id,
      });
    }
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    // Make request for tabs 0 and 1 (Open and Closed corrective actions)
    if (currentTab === 0 || currentTab === 1) {
      getActions({
        closed: currentTab !== 0,
        page: currentPage,
        date_to: actionsFilters?.date_to,
        date_from: actionsFilters?.date_from,
        search: actionsFilters?.search || '',
        facility_id: actionsFilters?.facility_id,
        equipment_id: actionsFilters?.equipment_id,
      });
    }
  }, [actionsFilters, currentPage, currentTab]);

  // Make requests for other tabs when their filters change
  useEffect(() => {
    if (currentTab === 3) {
      getMissedInspections({
        page: currentPage,
        tab_name: 'missed',
        completed: missedInspectionsFilters.completed,
        date_from: missedInspectionsFilters.date_from,
        date_to: missedInspectionsFilters.date_to,
        facility_id: missedInspectionsFilters.facility_id,
        equipment_type_id: missedInspectionsFilters.equipment_type_id,
        period: missedInspectionsFilters.period,
        search: missedInspectionsFilters.search || '',
      });
    }
  }, [missedInspectionsFilters, currentPage, currentTab, getMissedInspections]);

  useEffect(() => {
    if (currentTab === 6) {
      getMissedInspections({
        page: currentPage,
        tab_name: 'grace',
        completed: gracePeriodFilters.completed,
        date_from: gracePeriodFilters.date_from,
        date_to: gracePeriodFilters.date_to,
        facility_id: gracePeriodFilters.facility_id,
        equipment_type_id: gracePeriodFilters.equipment_type_id,
        period: gracePeriodFilters.period,
      });
    }
  }, [gracePeriodFilters, currentPage, currentTab, getMissedInspections]);

  useEffect(() => {
    if (currentTab === 5) {
      getReturnService({
        page: currentPage,
        completed: missedInspectionsFilters.completed,
        date_from: missedInspectionsFilters.date_from,
        date_to: missedInspectionsFilters.date_to,
        facility_id: missedInspectionsFilters.facility_id,
        equipment_type_id: missedInspectionsFilters.equipment_type_id,
        period: missedInspectionsFilters.period,
      });
    }
  }, [missedInspectionsFilters, currentPage, currentTab, getReturnService]);

  const updateMissedInspectionModal = () => {
    return (
      <div className='modal'>
        <form className='modal__content' onSubmit={updateMissedInspection}>
          <div className='modal__content-head'>
            <p>{t('correctiveActions.returnToServiceExplanation')}</p>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => setUpdateModal(false)}
            />
          </div>
          <div className='modal__content-body'>
            <label htmlFor='explanation' className='customInput'>
              {t('correctiveActions.explanation')}
              <textarea
                id='explanation'
                name='explanation'
                placeholder={t('correctiveActions.placeholders.explanation')}
                rows='4'
                onChange={handleUpdateIns}
                defaultValue={
                  currentMissedIns?.explanation
                    ? currentMissedIns?.explanation
                    : ''
                }
              />
            </label>
            <span className='eq-edit__label' style={{paddingBottom: 8}}>
              {t('correctiveActions.completedDate')}:
            </span>
            <div className='c-datepicker'>
              {currentMissedIns?.explanation ? (
                <DatePicker
                  selected={
                    updateInspectionData.completed_date
                      ? moment(updateInspectionData.completed_date).toDate()
                      : null
                  }
                  onChange={handleUpdateIns}
                  placeholderText={t(
                    'correctiveActions.placeholders.selectDate',
                  )}
                />
              ) : (
                <DatePicker
                  selected={
                    updateInspectionData.completed_date
                      ? moment(updateInspectionData.completed_date).toDate()
                      : null
                  }
                  onChange={handleUpdateIns}
                  placeholderText={t(
                    'correctiveActions.placeholders.selectDate',
                  )}
                />
              )}

              <span>
                <img src='/assets/icons/calendar.svg' alt='calendar' />
              </span>
            </div>
          </div>

          <div className='modal__content-bottom'>
            <button className='close' onClick={() => setUpdateModal(false)}>
              {t('correctiveActions.close')}
            </button>
            <button className='save' onClick={updateMissedInspection}>
              {t('correctiveActions.save')}
            </button>
          </div>
        </form>
        <div className='modal__bg'></div>
      </div>
    );
  };

  const renderFilters = () => {
    return (
      <div className='actions__filters'>
        <Input
          type='text'
          name={'search'}
          id={'search'}
          onChange={handleSearch}
          image='/assets/icons/search.svg'
          placeholder={t('correctiveActions.filters.enterEqUserComment')}
          defaultValue={search}
          maxwidth={200}
          innerRef={ref}
        />
        {/*<Input type="text" image='/assets/icons/filter.svg' placeholder="Quick filters" maxwidth={100} />*/}
        <Datepicker
          type={'from'}
          setValue={handleFilterDateFrom}
          value={urlParams.get('date_from')}
        />
        <Datepicker
          type={'to'}
          setValue={handleFilterDateTo}
          value={urlParams.get('date_to')}
        />
        <Select
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
              borderRadius: 8,
              width: 200,
              height: 42,
              backgroundColor: '#F4F5F6',
            }),
          }}
          isClearable
          key={JSON.stringify(facilityOptions) + '1'}
          placeholder={t('correctiveActions.filters.facility')}
          options={facilityOptions}
          defaultValue={
            facilityOptions?.filter(
              item => item?.value == urlParams.get('facility_id'),
            )[0]
          }
          onChange={e => {
            const facilityId = e?.value || null;
            setActionsFilters(old => ({...old, facility_id: facilityId}));
            setCurrentPage(1);
            navigate(
              `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${facilityId ? `&facility_id=${facilityId}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
            );
          }}
        />
        <Select
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
              borderRadius: 8,
              width: 200,
              height: 42,
              backgroundColor: '#F4F5F6',
            }),
          }}
          isClearable
          isSearchable
          filterOption={() => true}
          onInputChange={handleEquipmentInputChange}
          onBlur={() => setEquipmentSearch('')}
          isLoading={equipmentIsLoading}
          key='equipment-filter'
          placeholder={t('correctiveActions.placeholders.equipment')}
          options={equipmentOptions}
          value={
            equipmentOptions?.find(
              item => item?.value == urlParams.get('equipment_id') || item?.value == actionsFilters.equipment_id,
            ) ?? null
          }
          onChange={e => {
            setActionsFilters(old => ({...old, equipment_id: e?.value}));
            setCurrentPage(1);
            navigate(
              `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${e?.value ? `&equipment_id=${e?.value}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
            );
          }}
        />
      </div>
    );
  };

  const serviceModal = () => {
    return (
      <div className='modal'>
        <form className='modal__content service' style={{maxWidth: 1000}}>
          <div className='modal__content-head'>
            <p>{t('correctiveActions.outOfService')}</p>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => setServiceModalStatus(false)}
            />
          </div>
          <div className='modal__content-body services'>
            {currentService?.decommission_reason_message ? (
              <div
                style={{
                  marginBottom: 12,
                  padding: '10px 12px',
                  borderRadius: 8,
                  backgroundColor: '#F4F5F6',
                }}>
                <p
                  style={{
                    margin: '0 0 4px 0',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#667085',
                  }}>
                  {t('correctiveActions.tableHeaders.reason')}
                </p>
                <p style={{margin: 0, color: '#344054'}}>
                  {currentService?.decommission_reason_message}
                </p>
              </div>
            ) : null}
            {!currentService?.corrective_actions?.length &&
            !currentService?.service_requests?.length &&
            !currentService?.missed_inspections?.length ? (
              <p style={{textAlign: 'center', padding: 10}}>
                {t('correctiveActions.noElements')}
              </p>
            ) : null}
            {currentService?.corrective_actions?.length ? (
              <>
                <p className='modal__content-serviceTitle'>
                  {t('correctiveActions.correctiveActions')}
                </p>
                <div className='modal__content-serviceTable'>
                  <div className='modal__content-serviceHead'>
                    <p className='modal__content-serviceText header'>
                      {t('correctiveActions.categoryTitle')}
                    </p>
                    <p className='modal__content-serviceText header'>
                      {t('correctiveActions.period')}
                    </p>
                    {currentService?.corrective_actions?.some(
                      item => item?.discrepancy,
                    ) ? (
                      <p className='modal__content-serviceText header'>
                        {t('correctiveActions.discrepancy')}
                      </p>
                    ) : (
                      <p className='modal__content-serviceText header'>
                        {t('correctiveActions.comment')}
                      </p>
                    )}
                    <p className='modal__content-serviceText header'>
                      {t('correctiveActions.date')}
                    </p>
                  </div>
                  {currentService?.corrective_actions?.map(item => (
                    <div className='modal__content-service' key={item?.id}>
                      <p className='modal__content-serviceText'>
                        {item?.category_title || '---'}
                      </p>
                      <p className='modal__content-serviceText'>
                        {item?.inspection_period || '---'}
                      </p>
                      {item?.discrepancy && (
                        <p className='modal__content-serviceText'>
                          {item?.discrepancy || '---'}
                        </p>
                      )}
                      {item?.comment && (
                        <p className='modal__content-serviceText'>
                          {item?.comment || '---'}
                        </p>
                      )}
                      <p className='modal__content-serviceText'>
                        {moment(item.created_at).format('MM-DD-YY') || '---'}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {currentService?.service_requests?.length ? (
              <>
                <p className='modal__content-serviceTitle'>
                  {t('correctiveActions.serviceRequests')}
                </p>
                <div className='modal__content-serviceTable'>
                  <div className='modal__content-serviceHead'>
                    <p className='modal__content-serviceText header'>
                      {t('correctiveActions.comment')}
                    </p>
                    <p className='modal__content-serviceText header'>
                      {t('correctiveActions.status')}
                    </p>
                    <p className='modal__content-serviceText header'>
                      {t('correctiveActions.date')}
                    </p>
                  </div>
                  {currentService?.service_requests?.map(item => (
                    <div className='modal__content-service' key={item?.id}>
                      <p className='modal__content-serviceText'>
                        {item?.comment}
                      </p>
                      <p
                        className={`modal__content-serviceText ${item?.status}`}
                      >
                        {item?.status}
                      </p>
                      <p className='modal__content-serviceText'>
                        {moment(item.created_at).format('MM-DD-YY')}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {currentService?.missed_inspections?.length ? (
              <>
                <p className='modal__content-serviceTitle'>
                  {t('correctiveActions.returnToServiceInspections')}
                </p>
                <div className='modal__content-serviceTable'>
                  <div className='modal__content-serviceHead'>
                    <p className='modal__content-serviceText header'>
                      {t('correctiveActions.name')}
                    </p>
                    <p className='modal__content-serviceText header'>
                      {t('correctiveActions.period')}
                    </p>
                    <p className='modal__content-serviceText header'>
                      {t('correctiveActions.inspectionDate')}
                    </p>
                    <p className='modal__content-serviceText header'>
                      {t('correctiveActions.dueDate')}
                    </p>
                  </div>
                  {currentService?.missed_inspections?.map(item => (
                    <div className='modal__content-service' key={item?.id}>
                      <p className='modal__content-serviceText'>{item?.name}</p>
                      <p className={`modal__content-serviceText`}>
                        {item?.period?.charAt(0).toUpperCase() +
                          item?.period?.slice(1)}
                      </p>
                      <p className='modal__content-serviceText'>
                        {moment(item?.inspection_date).format('MM-DD-YY')}
                      </p>
                      <p className='modal__content-serviceText'>
                        {moment(item?.due_date).format('MM-DD-YY')}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          <div className='modal__content-bottom'>
            {/*<button className="close" onClick={() => setServiceModalStatus(false)}>*/}
            {/*  Close*/}
            {/*</button>*/}
            {/*<button className="save" onClick={updateMissedInspection}>*/}
            {/*  Save*/}
            {/*</button>*/}
          </div>
        </form>
        <div className='modal__bg'></div>
      </div>
    );
  };

  const tableRowsData = [
    actionsData?.data,
    actionsData?.data,
    [],
    getMissedInspectionsData?.data,
    outOfServiceData,
    getReturnServiceData?.data,
    getMissedInspectionsData?.data,
  ];

  const pages = [
    // Open items
    actionsData?.pages,
    // Closed items
    actionsData?.pages,
    0,
    // Missed insepctions
    getMissedInspectionsData?.pages,
    // Return to Service inspections
    0,
    getReturnServiceData?.pages,
    // Grace Perios
    getMissedInspectionsData?.pages,
  ];

  return (
    <Wrapper>
      <Helmet>
        <title>{t('correctiveActions.pageTitle')}</title>
      </Helmet>
      {updateModal && updateMissedInspectionModal()}
      {modalStatus && (
        <CorrectiveActionModal
          exit={() => setModalStatus(false)}
          action={chosenCorrectiveAction}
          currentTab={currentTab}
        />
      )}
      {serviceModalStatus && serviceModal()}
      <DashboardHeader title={t('correctiveActions.title')} type='actions' />
      <div className='actions'>
        <div className='actions__header'>
          <p
            className={`actions__header-tab ${currentTab == 0 ? 'active' : ''}`}
            onClick={() => {
              changeTab(0);
              setCurrentPage(1);
              navigate(
                `/actions?tab_id=0${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
              );
            }}
          >
            {t('correctiveActions.tabs.openItems')}
          </p>
          <p
            className={`actions__header-tab ${currentTab == 1 ? 'active' : ''}`}
            onClick={() => {
              changeTab(1);
              setCurrentPage(1);
              navigate(
                `/actions?tab_id=1${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
              );
            }}
          >
            {t('correctiveActions.tabs.closedItems')}
          </p>
          {/*<p className={`actions__header-tab ${currentTab === 2 ? 'active' : ''}`} onClick={()=>changeTab(2)}>Return To Service</p>*/}
          <p
            className={`actions__header-tab ${currentTab == 3 ? 'active' : ''}`}
            onClick={() => {
              changeTab(3);
              setCurrentPage(1);
              navigate(
                `/actions?tab_id=3${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
              );
            }}
          >
            {t('correctiveActions.tabs.missedInspections')}
          </p>

          <p
            className={`actions__header-tab ${currentTab == 5 ? 'active' : ''}`}
            onClick={() => {
              changeTab(5);
              setCurrentPage(1);
              navigate(
                `/actions?tab_id=5${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
              );
            }}
          >
            {t('correctiveActions.tabs.returnToServiceInspections')}
          </p>

          <p
            className={`actions__header-tab ${currentTab == 4 ? 'active' : ''}`}
            onClick={() => {
              changeTab(4);
              setCurrentPage(1);
              navigate(
                `/actions?tab_id=4${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
              );
            }}
          >
            {t('correctiveActions.tabs.outOfService')}
          </p>

          <p
            className={`actions__header-tab ${currentTab == 6 ? 'active' : ''}`}
            onClick={() => {
              changeTab(6);
              setCurrentPage(1);
              navigate(
                `/actions?tab_id=6${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
              );
            }}
          >
            {t('correctiveActions.tabs.gracePeriod')}
          </p>
        </div>
        {currentTab === 4 ? null : currentTab === 0 ? (
          renderFilters()
        ) : currentTab === 1 ? (
          renderFilters()
        ) : currentTab === 6 ? (
          <div className='actions__filters'>
            <Datepicker
              type={'from'}
              value={urlParams.get('date_missed_from')}
              setValue={date => {
                setGracePeriodFilters(old => ({
                  ...old,
                  date_from: date ? moment(date).format('YYYY-MM-DD') : null,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${date ? `&date_grace_from=${moment(date).format('YYYY-MM-DD')}` : ''}${urlParams.get('date_grace_to') ? `&date_grace_to=${urlParams.get('date_grace_to')}` : ''}`,
                );
              }}
            />
            <Datepicker
              type={'to'}
              value={urlParams.get('date_missed_to')}
              setValue={date => {
                setGracePeriodFilters(old => ({
                  ...old,
                  date_to: date ? moment(date).format('YYYY-MM-DD') : null,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_grace_from') ? `&date_grace_from=${urlParams.get('date_grace_from')}` : ''}${date ? `&date_grace_to=${moment(date).format('YYYY-MM-DD')}` : ''}`,
                );
              }}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              key={JSON.stringify(facilityOptions) + '1'}
              placeholder={t('correctiveActions.filters.facility')}
              options={facilityOptions}
              defaultValue={
                facilityOptions?.filter(
                  item => item?.value == urlParams.get('facility_id'),
                )[0]
              }
              onChange={e => {
                setGracePeriodFilters(old => ({
                  ...old,
                  facility_id: e?.value,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('period') ? `&period=${urlParams.get('period')}` : ''}${e?.value ? `&facility_id=${e?.value}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_grace_to') ? `&date_grace_to=${urlParams.get('date_grace_to')}` : ''}${urlParams.get('date_grace_from') ? `&date_grace_from=${urlParams.get('date_grace_from')}` : ''}`,
                );
              }}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              isSearchable
              filterOption={() => true}
              onInputChange={handleEquipmentTypeInputChange}
              isLoading={equipmentTypeIsLoading}
              key='equipment-type-filter'
              placeholder={t('correctiveActions.filters.equipmentType')}
              options={equipmentTypeOptions}
              value={
                equipmentTypeOptions?.find(
                  item => item?.value == urlParams.get('equipment_id') || item?.value == gracePeriodFilters.equipment_type_id,
                ) ?? null
              }
              onChange={e => {
                setGracePeriodFilters(old => ({
                  ...old,
                  equipment_type_id: e?.value,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('period') ? `&period=${urlParams.get('period')}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${e?.value ? `&equipment_id=${e?.value}` : ''}${urlParams.get('date_grace_to') ? `&date_grace_to=${urlParams.get('date_grace_to')}` : ''}${urlParams.get('date_grace_from') ? `&date_grace_from=${urlParams.get('date_grace_from')}` : ''}`,
                );
              }}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              key={JSON.stringify(periodOptions) + '2'}
              placeholder={t('correctiveActions.filters.period')}
              options={periodOptions}
              defaultValue={
                periodOptions?.filter(
                  item => item?.value == urlParams.get('period'),
                )[0]
              }
              onChange={e => {
                setGracePeriodFilters(old => ({...old, period: e?.value}));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${e?.value ? `&period=${e?.value}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${e?.value}` : ''}${urlParams.get('date_missed_to') ? `&date_grace_to=${urlParams.get('date_grace_to')}` : ''}${urlParams.get('date_grace_from') ? `&date_grace_from=${urlParams.get('date_grace_from')}` : ''}`,
                );
              }}
            />
          </div>
        ) : currentTab === 3 ? (
          <div className='actions__filters'>
            <Input
              type='text'
              name={'search'}
              id={'search'}
              onChange={handleSearchMissedInspections}
              image='/assets/icons/search.svg'
              placeholder={t('correctiveActions.filters.enterEqUserComment')}
              defaultValue={missedInspectionsFilters.search || ''}
              maxwidth={200}
              innerRef={ref}
            />
            <Datepicker
              type={'from'}
              value={urlParams.get('date_missed_from')}
              setValue={date => {
                setMissedInspectionsFilters(old => ({
                  ...old,
                  date_from: date ? moment(date).format('YYYY-MM-DD') : null,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${date ? `&date_missed_from=${moment(date).format('YYYY-MM-DD')}` : ''}${urlParams.get('date_missed_to') ? `&date_missed_to=${urlParams.get('date_missed_to')}` : ''}`,
                );
              }}
              width={200}
            />
            <Datepicker
              type={'to'}
              width={200}
              value={urlParams.get('date_missed_to')}
              setValue={date => {
                setMissedInspectionsFilters(old => ({
                  ...old,
                  date_to: date ? moment(date).format('YYYY-MM-DD') : null,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_missed_from') ? `&date_missed_from=${urlParams.get('date_missed_from')}` : ''}${date ? `&date_missed_to=${moment(date).format('YYYY-MM-DD')}` : ''}`,
                );
              }}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              key={JSON.stringify(facilityOptions) + '1'}
              placeholder={t('correctiveActions.filters.facility')}
              options={facilityOptions}
              defaultValue={
                facilityOptions?.filter(
                  item => item?.value == urlParams.get('facility_id'),
                )[0]
              }
              onChange={e => {
                setMissedInspectionsFilters(old => ({
                  ...old,
                  facility_id: e?.value,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('period') ? `&period=${urlParams.get('period')}` : ''}${e?.value ? `&facility_id=${e?.value}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_missed_to') ? `&date_missed_to=${urlParams.get('date_missed_to')}` : ''}${urlParams.get('date_missed_from') ? `&date_missed_from=${urlParams.get('date_missed_from')}` : ''}`,
                );
              }}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              isSearchable
              filterOption={() => true}
              onInputChange={handleEquipmentTypeInputChange}
              isLoading={equipmentTypeIsLoading}
              key='equipment-type-filter'
              placeholder={t('correctiveActions.filters.equipmentType')}
              options={equipmentTypeOptions}
              value={
                equipmentTypeOptions?.find(
                  item => item?.value == urlParams.get('equipment_id') || item?.value == missedInspectionsFilters.equipment_type_id,
                ) ?? null
              }
              onChange={e => {
                setMissedInspectionsFilters(old => ({
                  ...old,
                  equipment_type_id: e?.value,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('period') ? `&period=${urlParams.get('period')}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${e?.value ? `&equipment_id=${e?.value}` : ''}${urlParams.get('date_missed_to') ? `&date_missed_to=${urlParams.get('date_missed_to')}` : ''}${urlParams.get('date_missed_from') ? `&date_missed_from=${urlParams.get('date_missed_from')}` : ''}`,
                );
              }}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              key={JSON.stringify(periodOptions) + '2'}
              placeholder={t('correctiveActions.filters.period')}
              options={periodOptions}
              defaultValue={
                periodOptions?.filter(
                  item => item?.value == urlParams.get('period'),
                )[0]
              }
              onChange={e => {
                setMissedInspectionsFilters(old => ({
                  ...old,
                  period: e?.value,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${e?.value ? `&period=${e?.value}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${e?.value}` : ''}${urlParams.get('date_missed_to') ? `&date_missed_to=${urlParams.get('date_missed_to')}` : ''}${urlParams.get('date_missed_from') ? `&date_missed_from=${urlParams.get('date_missed_from')}` : ''}`,
                );
              }}
            />
          </div>
        ) : (
          <div className='actions__filters'>
            <Datepicker
              type={'from'}
              value={urlParams.get('date_missed_from')}
              setValue={date => {
                setMissedInspectionsFilters(old => ({
                  ...old,
                  date_from: date ? moment(date).format('YYYY-MM-DD') : null,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${date ? `&date_missed_from=${moment(date).format('YYYY-MM-DD')}` : ''}${urlParams.get('date_missed_to') ? `&date_missed_to=${urlParams.get('date_missed_to')}` : ''}`,
                );
              }}
            />
            <Datepicker
              type={'to'}
              value={urlParams.get('date_missed_to')}
              setValue={date => {
                setMissedInspectionsFilters(old => ({
                  ...old,
                  date_to: date ? moment(date).format('YYYY-MM-DD') : null,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_missed_from') ? `&date_missed_from=${urlParams.get('date_missed_from')}` : ''}${date ? `&date_missed_to=${moment(date).format('YYYY-MM-DD')}` : ''}`,
                );
              }}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              key={JSON.stringify(facilityOptions) + '1'}
              placeholder={t('correctiveActions.filters.facility')}
              options={facilityOptions}
              defaultValue={
                facilityOptions?.filter(
                  item => item?.value == urlParams.get('facility_id'),
                )[0]
              }
              onChange={e => {
                setMissedInspectionsFilters(old => ({
                  ...old,
                  facility_id: e?.value,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('period') ? `&period=${urlParams.get('period')}` : ''}${e?.value ? `&facility_id=${e?.value}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${urlParams.get('equipment_id')}` : ''}${urlParams.get('date_missed_to') ? `&date_missed_to=${urlParams.get('date_missed_to')}` : ''}${urlParams.get('date_missed_from') ? `&date_missed_from=${urlParams.get('date_missed_from')}` : ''}`,
                );
              }}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              isSearchable
              filterOption={() => true}
              onInputChange={handleEquipmentTypeInputChange}
              isLoading={equipmentTypeIsLoading}
              key='equipment-type-filter'
              placeholder={t('correctiveActions.filters.equipmentType')}
              options={equipmentTypeOptions}
              value={
                equipmentTypeOptions?.find(
                  item => item?.value == urlParams.get('equipment_id') || item?.value == missedInspectionsFilters.equipment_type_id,
                ) ?? null
              }
              onChange={e => {
                setMissedInspectionsFilters(old => ({
                  ...old,
                  equipment_type_id: e?.value,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${urlParams.get('period') ? `&period=${urlParams.get('period')}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${e?.value ? `&equipment_id=${e?.value}` : ''}${urlParams.get('date_missed_to') ? `&date_missed_to=${urlParams.get('date_missed_to')}` : ''}${urlParams.get('date_missed_from') ? `&date_missed_from=${urlParams.get('date_missed_from')}` : ''}`,
                );
              }}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              key={JSON.stringify(returnToServicePeriod) + '2'}
              placeholder={t('correctiveActions.filters.period')}
              options={returnToServicePeriod}
              defaultValue={
                returnToServicePeriod?.filter(
                  item => item?.value == urlParams.get('period'),
                )[0]
              }
              onChange={e => {
                setMissedInspectionsFilters(old => ({
                  ...old,
                  period: e?.value,
                }));
                setCurrentPage(1);
                navigate(
                  `/actions?${urlParams.get('tab_id') ? `tab_id=${urlParams.get('tab_id')}` : ''}${e?.value ? `&period=${e?.value}` : ''}${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('equipment_id') ? `&equipment_id=${e?.value}` : ''}${urlParams.get('date_missed_to') ? `&date_missed_to=${urlParams.get('date_missed_to')}` : ''}${urlParams.get('date_missed_from') ? `&date_missed_from=${urlParams.get('date_missed_from')}` : ''}`,
                );
              }}
            />
          </div>
        )}

        <div className='actions__box'>
          <Table
            headers={getHeaders(t)[currentTab]}
            rows={tableRowsData[currentTab]}
            cellKeys={cellKeys[currentTab]}
            customRowType={customRows[currentTab]}
            setChosenCorrectiveAction={setChosenCorrectiveAction}
            setModalStatus={setModalStatus}
            setCurrentMissedIns={setCurrentMissedIns}
            setServiceModalStatus={setServiceModalStatus}
            setCurrentService={setCurrentService}
            page={pages[currentTab]}
            handlePageClick={handlePageClick}
            currentPage={currentPage}
            withPagination
          />
        </div>
      </div>
    </Wrapper>
  );
}
